import { array, option } from 'fp-ts';
import { pipe } from 'fp-ts/function';
import { useRepositoryQuery } from '@mews-commander/data-access';
import { SelectField } from '@mews-commander/form';
import { createOption } from '@mews-commander/ui';
import { lt } from '@mews/framework';
import { Banner, Stack, Typography } from '@optimus-web/core';
import { useEnterprisesProductContext } from '../../../context/EnterprisesProductContext';
import { AccountingFieldName } from '../../forms/schemas/configureProductFormSchema';

interface Props {
    propertyId?: string;
}

export const AccountingForm = ({ propertyId }: Props) => {
    const {
        state: { selectedEnterpriseIds },
    } = useEnterprisesProductContext();

    const isEdit = !!propertyId;

    const { data: intersectedCategories, isFetching: intersectedCategoriesFetching } = useRepositoryQuery(
        'portfolioAccountingCategories',
        'getIntersectedKeys',
        {
            enterpriseIds: selectedEnterpriseIds,
        },
        { enabled: !isEdit }
    );

    const { data: enterpriseCategories, isFetching: enterpriseCategoriesFetching } = useRepositoryQuery(
        'accountingCategories',
        'getAll',
        {
            enterpriseIds: [propertyId || ''],
        },
        { enabled: isEdit }
    );

    const intersectedOptions = intersectedCategories?.keys?.map((key) =>
        createOption({
            value: key,
            label: key,
        })
    );

    const enterpriseOptions = enterpriseCategories?.accountingCategories
        ? pipe(
              enterpriseCategories.accountingCategories,
              array.filterMap(({ isActive, name, id }) =>
                  isActive
                      ? option.some(
                            createOption({
                                label: name,
                                value: id,
                            })
                        )
                      : option.none
              )
          )
        : [];

    const isMultipleProperties = selectedEnterpriseIds.length > 1;

    const selectOptions = isEdit ? enterpriseOptions : intersectedOptions;
    const isFetching = intersectedCategoriesFetching || enterpriseCategoriesFetching;
    const noOptions = selectOptions?.length === 0;

    return (
        <Stack spacing="300" vertical>
            {!isEdit && (
                <Stack spacing="200" vertical>
                    <Typography textStyle="titleSmallStrong" as="div">
                        {lt('ConfigureProductSubHeadingAccounting')}
                    </Typography>
                    {isMultipleProperties && (
                        <Typography textStyle="bodyMedium" variant="secondary" as="div">
                            {lt('ConfigureProductAccountingBody')}
                        </Typography>
                    )}
                    {noOptions &&
                        (isMultipleProperties ? (
                            <Banner
                                variant="info"
                                primaryText={lt('ConfigureProductAccountingUnavailableInfoBannerTitle')}
                                secondaryText={lt('ConfigureProductAccountingUnavailableInfoBannerBody')}
                            />
                        ) : (
                            <Banner
                                variant="info"
                                primaryText={lt('ConfigureProductAccountingUnavailableInfoBannerTitle2')}
                                secondaryText={lt('ConfigureProductAccountingUnavailableInfoBannerBody2')}
                            />
                        ))}
                </Stack>
            )}
            <SelectField
                name={AccountingFieldName.AccountingCategoryKey}
                options={selectOptions ?? []}
                label={lt('ConfigureProductAccountingCategoryDropdown')}
                loader={isFetching}
                disabled={noOptions}
            />
            <SelectField
                name={AccountingFieldName.RebateAccountingCategoryKey}
                options={selectOptions ?? []}
                label={lt('ConfigureProductRebateDropdown')}
                loader={isFetching}
                disabled={noOptions}
            />
            <SelectField
                name={AccountingFieldName.CancellationAccountingCategoryKey}
                options={selectOptions ?? []}
                label={lt('ConfigureProductCanceledDropdown')}
                loader={isFetching}
                disabled={noOptions}
            />
        </Stack>
    );
};
